<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import {
  dvMobileContactPanel,
  isDvSmallScreen,
} from 'dashboard/helper/dvMobilePanel';

const DV_ACCOUNT_ID = 1;

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();

    return {
      uiSettings,
      updateUISettings,
    };
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return (
        this.isContactPanelOpen &&
        !!this.currentChat.id &&
        !!this.uiSettings?.is_contact_sidebar_open &&
        dvMobileContactPanel.suppressedChatId !== this.currentChat.id
      );
    },
    isDvAccount() {
      return Number(this.$store.getters.getCurrentAccountId) === DV_ACCOUNT_ID;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler() {
        if (this.currentChat.inbox_id && this.currentChat.id) {
          this.$store.dispatch('inboxAssignableAgents/fetch', {
            inboxIds: [this.currentChat.inbox_id],
            includeAIAssignees: true,
          });
        }
      },
    },
    'currentChat.id': {
      immediate: true,
      handler(chatId, previousChatId) {
        // DV phone: open the conversation first, not the contact card.
        dvMobileContactPanel.suppressedChatId =
          this.isDvAccount &&
          chatId &&
          isDvSmallScreen() &&
          this.uiSettings?.is_contact_sidebar_open
            ? chatId
            : null;
        if (previousChatId === undefined) return;
        this.fetchLabels();
        this.activeIndex = 0;
      },
    },
    'uiSettings.is_contact_sidebar_open'(isOpen, wasOpen) {
      if (dvMobileContactPanel.suppressedChatId === null) return;
      dvMobileContactPanel.suppressedChatId = null;
      // The toggle showed "open" while the card was hidden: its tap means "show".
      if (!isOpen && wasOpen) {
        this.updateUISettings({ is_contact_sidebar_open: true });
      }
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
      'conversation-details-wrap--contact-sidebar-open': showContactPanel,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div v-show="!activeIndex" class="conversation-body flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      class="conversation-dashboard-app"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>

<style scoped>
@media (min-width: 1024px) {
  .conversation-details-wrap--contact-sidebar-open .conversation-body,
  .conversation-details-wrap--contact-sidebar-open .conversation-dashboard-app {
    padding-right: var(--dv-contact-sidebar-width, 320px);
    transition: padding-right 180ms ease;
  }

  :global([dir='rtl'])
    .conversation-details-wrap--contact-sidebar-open
    .conversation-body,
  :global([dir='rtl'])
    .conversation-details-wrap--contact-sidebar-open
    .conversation-dashboard-app {
    padding-right: 0;
    padding-left: var(--dv-contact-sidebar-width, 320px);
    transition:
      padding-left 180ms ease,
      padding-right 180ms ease;
  }
}

@media (min-width: 1536px) {
  .conversation-details-wrap--contact-sidebar-open .conversation-body,
  .conversation-details-wrap--contact-sidebar-open .conversation-dashboard-app {
    --dv-contact-sidebar-width: 360px;
  }
}
</style>
